import {
  getUsersGetMyAlertsQueryKey,
  useAlertsMarkAsRead,
  useAlertsMarkMultipleAsRead,
  useUsersGetMyAlerts,
} from '@/api/WhisApi/generated';
import { EmptyStateView } from '@components/EmptyStateView/EmptyStateView';
import { LoadingView } from '@components/LoadingView/LoadingView';
import { ScreenHeader } from '@components/ScreenHeader';
import { TabScreenContainer } from '@components/TabScreenContainer';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { NotificationListItem } from '@screens/Notifications/components/NotificationListItem';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigation } from 'expo-router';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { Pressable } from 'react-native';
import { ScrollView, Text, XStack, YStack } from 'tamagui';

export default function NotificationsScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const { data, isPending } = useUsersGetMyAlerts();
  const { mutate: markAlertAsRead, isPending: isMarkingAlertAsRead } =
    useAlertsMarkAsRead();
  const { mutate: markAllAlertsAsRead, isPending: isMarkingAllAlertsAsRead } =
    useAlertsMarkMultipleAsRead();

  const alerts = data?.alerts ?? [];
  const hasUnreadAlerts = alerts.some((alert) => !alert.read);

  const handleAlertPress = (alertId: string, isRead: boolean): void => {
    if (isRead) {
      return;
    }

    markAlertAsRead(
      { alertId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getUsersGetMyAlertsQueryKey(),
          });
        },
        onError: (error) => {
          console.error('[Alerts] failed to mark alert as read', error);
        },
      },
    );
  };

  const handleMarkAllAsRead = useCallback((): void => {
    markAllAlertsAsRead(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getUsersGetMyAlertsQueryKey(),
        });
      },
      onError: (error) => {
        console.error('[Alerts] failed to mark all alerts as read', error);
      },
    });
  }, [markAllAlertsAsRead, queryClient]);

  const markAllAction = useMemo(
    () => (
      <Pressable
        disabled={!hasUnreadAlerts || isMarkingAllAlertsAsRead}
        onPress={handleMarkAllAsRead}
      >
        <XStack paddingHorizontal="$2">
          <Text color="$brand" fontFamily="$bold">
            {t('alerts.markAllAsRead')}
          </Text>
        </XStack>
      </Pressable>
    ),
    [hasUnreadAlerts, isMarkingAllAlertsAsRead, handleMarkAllAsRead, t],
  );

  useEffect(() => {
    navigation.setOptions({
      headerTitle: t('navigation.alerts'),
      title: t('navigation.alerts'),
      headerRight: isSmallScreen ? (): ReactNode => markAllAction : undefined,
    });
  }, [isSmallScreen, markAllAction, navigation, t]);

  if (isPending) {
    return <LoadingView />;
  }

  if (!alerts.length) {
    return (
      <TabScreenContainer>
        {!isSmallScreen && <ScreenHeader title={t('navigation.alerts')} />}
        <EmptyStateView title={t('alerts.emptyTitle')} />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      {!isSmallScreen && (
        <ScreenHeader title={t('navigation.alerts')} actions={markAllAction} />
      )}

      <ScrollView showsVerticalScrollIndicator={false}>
        <YStack gap="$3">
          {alerts.map((alert) => (
            <NotificationListItem
              key={alert.id}
              alert={alert}
              onPress={(): void => handleAlertPress(alert.id, alert.read)}
              disabled={isMarkingAlertAsRead || isMarkingAllAlertsAsRead}
            />
          ))}
        </YStack>
      </ScrollView>
    </TabScreenContainer>
  );
}
